import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardApi, turfCutsApi, maintenanceApi, locationsApi, rainfallApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tractor, MapPin, Scissors, SprayCan, Package, Cloud, Wind, Droplets, Sun, CloudRain, CloudSnow, CloudFog, Bell, AlertTriangle, ExternalLink } from 'lucide-react';
import Map, { Source, Layer, NavigationControl, Marker } from 'react-map-gl/maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';

const weatherIcons = {
  Clear: Sun,
  Clouds: Cloud,
  Rain: CloudRain,
  Drizzle: CloudRain,
  Snow: CloudSnow,
  Mist: CloudFog,
  Fog: CloudFog,
};

// Farm location for weather (Queensland, Australia)
const FARM_LOCATION = { lat: -27.943, lon: 153.137 };

const MAP_STYLE = {
  version: 8,
  sources: {
    'esri-satellite': {
      type: 'raster',
      tiles: ['https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'],
      tileSize: 256,
      attribution: '&copy; Esri'
    }
  },
  layers: [{ id: 'esri-satellite', type: 'raster', source: 'esri-satellite', minzoom: 0, maxzoom: 19 }]
};

export function DashboardPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [weather, setWeather] = useState(null);
  const [recentCuts, setRecentCuts] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [locations, setLocations] = useState([]);
  const [farmOverview, setFarmOverview] = useState(null);
  const [rainfallSummary, setRainfallSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCut, setSelectedCut] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [statsRes, weatherRes, cutsRes, remindersRes, locsRes, overviewRes, rainfallRes] = await Promise.all([
        dashboardApi.getStats(),
        dashboardApi.getWeather(FARM_LOCATION.lat, FARM_LOCATION.lon),
        turfCutsApi.getAll(),
        maintenanceApi.getReminders(),
        locationsApi.getAll(),
        dashboardApi.getFarmOverview(),
        rainfallApi.getSummary(),
      ]);
      setStats(statsRes.data);
      setWeather(weatherRes.data);
      setRecentCuts(cutsRes.data.slice(0, 5));
      setReminders(remindersRes.data);
      setLocations(locsRes.data);
      setFarmOverview(overviewRes.data);
      setRainfallSummary(rainfallRes.data);
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openCutDetail = (cut) => {
    setSelectedCut(cut);
    setDetailOpen(true);
  };

  const getLocationBoundary = (locationId) => {
    const loc = locations.find(l => l.id === locationId);
    return loc?.coordinates?.length >= 3 ? loc.coordinates : null;
  };

  const getCutMapCenter = (cut) => {
    const pts = cut.cut_points?.length > 0 ? cut.cut_points : getLocationBoundary(cut.location_id);
    if (!pts || pts.length === 0) return { longitude: FARM_LOCATION.lon, latitude: FARM_LOCATION.lat, zoom: 15 };
    const lngs = pts.map(p => p[0]);
    const lats = pts.map(p => p[1]);
    return {
      longitude: (Math.min(...lngs) + Math.max(...lngs)) / 2,
      latitude: (Math.min(...lats) + Math.max(...lats)) / 2,
      zoom: 17,
    };
  };

  const WeatherIcon = weather?.weather?.[0]?.main ? weatherIcons[weather.weather[0].main] || Cloud : Cloud;

  if (loading) {
    return (
      <div className="animate-pulse space-y-6" data-testid="dashboard-loading">
        <div className="h-8 w-48 bg-muted rounded" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-32 bg-muted rounded-md" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground">Dashboard</h1>
          <p className="text-muted-foreground">Overview of your turf farm operations</p>
        </div>
      </div>

      {/* Weather Widget */}
      <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20" data-testid="weather-widget">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <WeatherIcon className="h-16 w-16 text-primary weather-icon" />
              <div>
                <p className="text-4xl font-heading font-bold text-foreground" data-testid="weather-temp">
                  {Math.round(weather?.main?.temp || 0)}°C
                </p>
                <p className="text-muted-foreground capitalize">
                  {weather?.weather?.[0]?.description || 'Unknown'}
                </p>
                <p className="text-sm text-muted-foreground">{weather?.name || 'Dublin'}</p>
                {weather?.mock && <Badge variant="secondary" className="mt-1">Demo Data</Badge>}
              </div>
            </div>
            <div className="hidden sm:flex gap-8">
              <div className="flex items-center gap-2">
                <Droplets className="h-5 w-5 text-blue-500" />
                <div>
                  <p className="text-sm text-muted-foreground">Humidity</p>
                  <p className="font-semibold">{weather?.main?.humidity || 0}%</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Wind className="h-5 w-5 text-gray-500" />
                <div>
                  <p className="text-sm text-muted-foreground">Wind</p>
                  <p className="font-semibold">{weather?.wind?.speed || 0} m/s</p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="stat-card" data-testid="stat-machinery">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Machinery</CardTitle>
            <Tractor className="h-5 w-5 text-primary" />
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-heading font-bold">{stats?.machinery?.total || 0}</p>
            <p className="text-sm text-muted-foreground">
              {stats?.machinery?.operational || 0} operational
            </p>
          </CardContent>
        </Card>

        <Card className="stat-card" data-testid="stat-locations">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Locations</CardTitle>
            <MapPin className="h-5 w-5 text-primary" />
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-heading font-bold">{stats?.locations || 0}</p>
            <p className="text-sm text-muted-foreground">Active fields</p>
          </CardContent>
        </Card>

        <Card className="stat-card" data-testid="stat-turf-cuts">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Turf Cuts (30d)</CardTitle>
            <Scissors className="h-5 w-5 text-primary" />
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-heading font-bold">{stats?.turf_cuts?.recent_sqm?.toLocaleString() || 0}</p>
            <p className="text-sm text-muted-foreground">sqm harvested</p>
          </CardContent>
        </Card>

        <Card className="stat-card" data-testid="stat-stock">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Low Stock</CardTitle>
            <Package className="h-5 w-5 text-accent" />
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-heading font-bold text-accent">{stats?.low_stock_count || 0}</p>
            <p className="text-sm text-muted-foreground">Items need restocking</p>
          </CardContent>
        </Card>
      </div>

      {/* Rainfall Widget */}
      <Card className="border-blue-200 bg-blue-50/30" data-testid="rainfall-widget">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <CloudRain className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="font-heading font-bold text-lg text-foreground">Rainfall</p>
                <p className="text-xs text-muted-foreground">
                  {rainfallSummary?.last_entry ? `Last recorded: ${rainfallSummary.last_entry}` : 'No entries yet'}
                </p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => navigate('/rainfall')} className="text-xs" data-testid="view-rainfall-btn">
              View All
            </Button>
          </div>
          <div className="grid grid-cols-3 gap-4 mt-4">
            <div className="text-center p-2 bg-white rounded-lg border">
              <p className="text-xs text-muted-foreground">7 Days</p>
              <p className="font-heading font-bold text-lg text-blue-700">{rainfallSummary?.last_7_days || 0}<span className="text-xs font-normal"> mm</span></p>
            </div>
            <div className="text-center p-2 bg-white rounded-lg border">
              <p className="text-xs text-muted-foreground">30 Days</p>
              <p className="font-heading font-bold text-lg text-blue-700">{rainfallSummary?.last_30_days || 0}<span className="text-xs font-normal"> mm</span></p>
            </div>
            <div className="text-center p-2 bg-white rounded-lg border">
              <p className="text-xs text-muted-foreground">Year</p>
              <p className="font-heading font-bold text-lg text-blue-700">{rainfallSummary?.year_to_date || 0}<span className="text-xs font-normal"> mm</span></p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Farm Map Widget */}
      {farmOverview?.fields?.some(f => f.coordinates?.length >= 3) && (
        <Card data-testid="farm-map-widget">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <MapPin className="h-5 w-5 text-primary" /> Farm Overview
            </CardTitle>
            <Button size="sm" variant="outline" onClick={() => navigate('/farm-map')} data-testid="open-farm-map-btn">
              <ExternalLink className="h-3 w-3 mr-1" /> Full Map
            </Button>
          </CardHeader>
          <CardContent>
            <div className="h-[300px] rounded-lg overflow-hidden border">
              {(() => {
                const allPts = [];
                farmOverview.fields.forEach(f => { if (f.coordinates?.length > 0) allPts.push(...f.coordinates); });
                const lngs = allPts.map(p => p[0]); const lats = allPts.map(p => p[1]);
                const center = { longitude: (Math.min(...lngs) + Math.max(...lngs)) / 2, latitude: (Math.min(...lats) + Math.max(...lats)) / 2, zoom: 16 };
                const CUT_COLORS = ['#C026D3', '#9333EA', '#7C3AED', '#6366F1', '#3B82F6', '#06B6D4', '#10B981', '#F59E0B'];
                return (
                  <Map initialViewState={center} mapStyle={MAP_STYLE} style={{ width: '100%', height: '100%' }}>
                    <NavigationControl position="top-right" />
                    {farmOverview.fields.filter(f => f.coordinates?.length >= 3).map((field) => (
                      <Source key={`db-field-${field.id}`} id={`db-field-${field.id}`} type="geojson"
                        data={{ type: 'Feature', geometry: { type: 'Polygon', coordinates: [[...field.coordinates, field.coordinates[0]]] } }}>
                        <Layer id={`db-field-fill-${field.id}`} type="fill" paint={{ 'fill-color': '#1B4332', 'fill-opacity': 0.1 }} />
                        <Layer id={`db-field-line-${field.id}`} type="line" paint={{ 'line-color': '#EF4444', 'line-width': 2, 'line-dasharray': [4, 3] }} />
                      </Source>
                    ))}
                    {farmOverview.fields.map((field) =>
                      field.cut_polygons?.filter(cp => cp.points?.length >= 3).map((cp, cpIdx) => (
                        <Source key={`db-cut-${field.id}-${cpIdx}`} id={`db-cut-${field.id}-${cpIdx}`} type="geojson"
                          data={{ type: 'Feature', geometry: { type: 'Polygon', coordinates: [[...cp.points, cp.points[0]]] } }}>
                          <Layer id={`db-cut-fill-${field.id}-${cpIdx}`} type="fill" paint={{ 'fill-color': CUT_COLORS[cpIdx % CUT_COLORS.length], 'fill-opacity': 0.4 }} />
                        </Source>
                      ))
                    )}
                    {farmOverview.fields.filter(f => f.coordinates?.length >= 3).map((field) => {
                      const lngs2 = field.coordinates.map(p => p[0]); const lats2 = field.coordinates.map(p => p[1]);
                      const cLng = (Math.min(...lngs2) + Math.max(...lngs2)) / 2; const cLat = (Math.min(...lats2) + Math.max(...lats2)) / 2;
                      return (
                        <Marker key={`db-label-${field.id}`} longitude={cLng} latitude={cLat} anchor="center">
                          <div className="px-1.5 py-0.5 rounded bg-black/70 text-white text-[10px] font-semibold shadow whitespace-nowrap">{field.name}</div>
                        </Marker>
                      );
                    })}
                  </Map>
                );
              })()}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Maintenance Reminders */}
      {reminders.length > 0 && (
        <Card className="border-accent bg-accent/5" data-testid="maintenance-reminders-card">
          <CardHeader>
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <Bell className="h-5 w-5 text-accent" />
              Maintenance Reminders
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {reminders.map((reminder) => (
                <div 
                  key={reminder.id} 
                  className={`flex items-center justify-between p-3 rounded-md ${
                    reminder.status === 'overdue' ? 'bg-destructive/10 border border-destructive/20' : 'bg-secondary/30'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {reminder.status === 'overdue' && <AlertTriangle className="h-5 w-5 text-destructive" />}
                    <div>
                      <p className="font-medium">{reminder.machinery_name}</p>
                      <p className="text-sm text-muted-foreground">{reminder.type}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium">Due: {reminder.due_date}</p>
                    <Badge variant={reminder.status === 'overdue' ? 'destructive' : 'secondary'}>
                      {reminder.status === 'overdue' ? 'Overdue' : 'Upcoming'}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Turf Cuts */}
      <Card data-testid="recent-cuts-card">
        <CardHeader>
          <CardTitle className="font-heading text-xl flex items-center gap-2">
            <Scissors className="h-5 w-5 text-primary" />
            Recent Turf Cuts
          </CardTitle>
        </CardHeader>
        <CardContent>
          {recentCuts.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">No turf cuts recorded yet</p>
          ) : (
            <div className="space-y-3">
              {recentCuts.map((cut) => (
                <div 
                  key={cut.id} 
                  className="flex items-center justify-between p-3 bg-secondary/30 rounded-md cursor-pointer hover:bg-secondary/50 transition-colors"
                  onClick={() => openCutDetail(cut)}
                  data-testid={`dashboard-cut-${cut.id}`}
                >
                  <div>
                    <p className="font-medium">{cut.location_name || 'Unknown Location'}</p>
                    <p className="text-sm text-muted-foreground">{cut.date}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold">{cut.quantity_sqm} sqm</p>
                    <Badge className={`quality-${cut.quality}`}>{cut.quality}</Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Turf Cut Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[95vh] overflow-y-auto" data-testid="dashboard-cut-detail-dialog">
          {selectedCut && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl flex items-center gap-3">
                  <Scissors className="h-5 w-5 text-primary" />
                  Turf Cut - {selectedCut.date}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Field</p>
                    <p className="font-medium" data-testid="dash-detail-field">{selectedCut.location_name || 'Unknown'}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Quantity</p>
                    <p className="font-medium font-mono" data-testid="dash-detail-qty">{selectedCut.quantity_sqm.toLocaleString()} sqm</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Quality</p>
                    <Badge className={`quality-${selectedCut.quality}`} data-testid="dash-detail-quality">{selectedCut.quality}</Badge>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Recorded By</p>
                    <p className="font-medium">{selectedCut.created_by || '-'}</p>
                  </div>
                </div>

                {selectedCut.notes && (
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Notes</p>
                    <p className="bg-secondary/20 p-3 rounded-lg border">{selectedCut.notes}</p>
                  </div>
                )}

                {/* Map */}
                {(selectedCut.cut_points?.length > 0 || getLocationBoundary(selectedCut.location_id)) && (
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Cut Location</p>
                    <div className="h-[300px] rounded-lg overflow-hidden border" data-testid="dash-detail-map">
                      <Map
                        initialViewState={getCutMapCenter(selectedCut)}
                        mapStyle={MAP_STYLE}
                        style={{ width: '100%', height: '100%' }}
                      >
                        <NavigationControl position="top-right" />

                        {getLocationBoundary(selectedCut.location_id) && (
                          <Source
                            id="dash-field"
                            type="geojson"
                            data={{
                              type: 'Feature',
                              geometry: {
                                type: 'Polygon',
                                coordinates: [[...getLocationBoundary(selectedCut.location_id), getLocationBoundary(selectedCut.location_id)[0]]],
                              },
                            }}
                          >
                            <Layer id="dash-field-fill" type="fill" paint={{ 'fill-color': '#1B4332', 'fill-opacity': 0.15 }} />
                            <Layer id="dash-field-line" type="line" paint={{ 'line-color': '#1B4332', 'line-width': 2, 'line-dasharray': [3, 2] }} />
                          </Source>
                        )}

                        {selectedCut.cut_points?.length >= 3 && (
                          <Source
                            id="dash-cut"
                            type="geojson"
                            data={{
                              type: 'Feature',
                              geometry: {
                                type: 'Polygon',
                                coordinates: [[...selectedCut.cut_points, selectedCut.cut_points[0]]],
                              },
                            }}
                          >
                            <Layer id="dash-cut-fill" type="fill" paint={{ 'fill-color': '#F59E0B', 'fill-opacity': 0.4 }} />
                            <Layer id="dash-cut-line" type="line" paint={{ 'line-color': '#F59E0B', 'line-width': 3 }} />
                          </Source>
                        )}

                        {selectedCut.cut_points?.map((point, idx) => (
                          <Marker key={idx} longitude={point[0]} latitude={point[1]}>
                            <div className="w-5 h-5 rounded-full bg-amber-500 border-2 border-white shadow-lg flex items-center justify-center text-white text-[10px] font-bold">
                              {idx + 1}
                            </div>
                          </Marker>
                        ))}
                      </Map>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      {selectedCut.cut_points?.length > 0 && (
                        <span className="flex items-center gap-1">
                          <span className="w-3 h-3 rounded-sm bg-amber-500 opacity-50" />
                          Cut area
                        </span>
                      )}
                      {getLocationBoundary(selectedCut.location_id) && (
                        <span className="flex items-center gap-1">
                          <span className="w-3 h-3 rounded-sm bg-[#1B4332] opacity-30" />
                          Field boundary
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
